import React, { useState, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { GET_ALL_PAGES_FOR_PICKER } from '../pageQueries'
import { useLanguage } from '@/context/LanguageContext'

interface PagePickerNode {
  id: string
  parentId: string | null
  arrangement: number
  page_profiles: { language: string; title?: string; slug?: string }[]
}

// Titles occasionally contain a literal "\n" — either a real newline or the
// two-character backslash-n sequence typed into the CMS field — which
// otherwise renders inline in the picker. Collapse both to a single space.
const sanitizeTitle = (title: string): string =>
  title.replace(/\\n|\n/g, ' ').replace(/\s+/g, ' ').trim()

interface PageParentSelectProps {
  value: string | null | undefined
  onChange: (value: string | null) => void
  excludeId?: string
  placeholder?: string
}

export function PageParentSelect({
  value,
  onChange,
  excludeId,
  placeholder = 'No parent (root page)',
}: PageParentSelectProps) {
  const [open, setOpen] = useState(false)
  const { language: currentLanguage } = useLanguage()

  const { data, loading } = useQuery(GET_ALL_PAGES_FOR_PICKER, {
    fetchPolicy: 'cache-and-network',
  })

  const pages: PagePickerNode[] = data?.findManyPage ?? []

  const getName = (profiles: { language: string; title?: string }[]) => {
    const raw =
      profiles.find(
        p => p.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.title ||
      profiles[0]?.title ||
      ''
    return sanitizeTitle(raw)
  }

  // Build lookup maps once
  const { byId, childrenByParent } = useMemo(() => {
    const byId = new Map<string, PagePickerNode>()
    const childrenByParent = new Map<string | null, PagePickerNode[]>()

    for (const page of pages) {
      byId.set(page.id, page)
      const parentKey = page.parentId ?? null
      if (!childrenByParent.has(parentKey)) {
        childrenByParent.set(parentKey, [])
      }
      childrenByParent.get(parentKey)!.push(page)
    }

    return { byId, childrenByParent }
  }, [pages])

  // Collect a page and all its descendants
  const collectDescendants = (startId: string): Set<string> => {
    const result = new Set<string>()
    const queue = [startId]
    while (queue.length > 0) {
      const current = queue.shift()!
      result.add(current)
      const children = childrenByParent.get(current) ?? []
      for (const child of children) {
        if (!result.has(child.id)) {
          queue.push(child.id)
        }
      }
    }
    return result
  }

  // Compute full path string for a page (e.g. "Products / Chairs")
  const getPath = (page: PagePickerNode): string => {
    const segments: string[] = []
    const visited = new Set<string>()
    let current: PagePickerNode | undefined = page

    while (current) {
      if (visited.has(current.id)) break
      visited.add(current.id)
      const title = getName(current.page_profiles)
      segments.unshift(title || current.id)
      const parentId = current.parentId ?? null
      current = parentId ? byId.get(parentId) : undefined
    }

    return segments.join(' / ')
  }

  const excludedIds: Set<string> = useMemo(() => {
    if (!excludeId) return new Set()
    return collectDescendants(excludeId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeId, childrenByParent])

  const availablePages = pages.filter(p => !excludedIds.has(p.id))

  // Order siblings the same way the Pages tree list does: by arrangement,
  // then by (current-language) title.
  const sortSiblings = (nodes: PagePickerNode[]): PagePickerNode[] => {
    return [...nodes].sort((a, b) => {
      if (a.arrangement !== b.arrangement) return a.arrangement - b.arrangement
      return getName(a.page_profiles).localeCompare(getName(b.page_profiles))
    })
  }

  // Flatten into proper tree (depth-first, each parent immediately followed
  // by its own children) instead of rendering in raw fetch order — fetch
  // order has no relationship to the parent/child hierarchy, so previously
  // a child could be listed above its parent while still being indented as
  // if nested under it.
  const pageTree: { page: PagePickerNode; depth: number }[] = useMemo(() => {
    const availableIds = new Set(availablePages.map(p => p.id))
    const childrenByAvailableParent = new Map<string | null, PagePickerNode[]>()
    for (const page of availablePages) {
      const key = page.parentId && availableIds.has(page.parentId) ? page.parentId : null
      if (!childrenByAvailableParent.has(key)) {
        childrenByAvailableParent.set(key, [])
      }
      childrenByAvailableParent.get(key)!.push(page)
    }

    const flattened: { page: PagePickerNode; depth: number }[] = []
    const visit = (parentId: string | null, depth: number, visited: Set<string>) => {
      const children = sortSiblings(childrenByAvailableParent.get(parentId) ?? [])
      for (const child of children) {
        if (visited.has(child.id)) continue
        visited.add(child.id)
        flattened.push({ page: child, depth })
        visit(child.id, depth + 1, visited)
      }
    }
    visit(null, 0, new Set())
    return flattened
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePages, currentLanguage])

  const selectedPage = pages.find(p => p.id === value)
  const selectedLabel = selectedPage ? getPath(selectedPage) : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selectedLabel || placeholder}
          </span>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {value && (
              <span
                role="button"
                className="inline-flex items-center justify-center"
                onPointerDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange(null)
                  setOpen(false)
                }}
              >
                <X className="h-4 w-4 opacity-50 hover:opacity-100" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search pages..." />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Loading...' : 'No pages found.'}
            </CommandEmpty>
            <CommandGroup>
              {pageTree.map(({ page, depth }) => {
                const pathLabel = getPath(page)
                const indent = depth * 16

                return (
                  <CommandItem
                    key={page.id}
                    value={`${page.id}-${pathLabel}`}
                    onSelect={() => {
                      onChange(page.id === value ? null : page.id)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value === page.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span style={{ paddingLeft: indent }} className="truncate">
                      {pathLabel}
                    </span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
